import { Box, CompassColor, Stack } from "@noom/wax-component-library";
import { useTranslation } from "react-i18next";

import { formatDateLongMonthDayYear } from "../../../utils/dateUtils";
import { buildAuthenticatedUrlDeeplink } from "../../../utils/urlUtils";
import { TreatmentProductLockup } from "../TreatmentProductLockup";
import { openDeeplink } from "../utils";

import { LabResultsCta } from "./LabResultsCta";
import type { LabResultsCardModel } from "./treatmentDetailViewModel";

type LabResultsCardProps = {
  resultsCard: LabResultsCardModel;
  medicationName: string;
  productIconUrl?: string | null;
};

const resolveTitle = (
  resultsCard: LabResultsCardModel,
  t: (key: string) => string
): string => {
  switch (resultsCard.type) {
    case "resultsPending":
      return t("detail.resultsCard.pending.title");
    case "lastResultsCollected":
      return t("detail.resultsCard.lastCollected.title");
    case "resultsReady":
      return t("detail.resultsCard.ready.title");
  }
};

const resolveViewResults = (
  resultsCard: LabResultsCardModel
): (() => void) | null => {
  const { viewResultsUrl } = resultsCard;
  if (!viewResultsUrl) {
    return null;
  }
  return () =>
    openDeeplink(
      buildAuthenticatedUrlDeeplink(viewResultsUrl, {
        navContext: "modal",
        titleSource: "fromDocument",
      })
    );
};

const resolveFormattedDate = (
  resultsCard: LabResultsCardModel
): string | null => {
  if (resultsCard.type === "resultsPending" || !resultsCard.resultsDate) {
    return null;
  }
  const formatted = formatDateLongMonthDayYear(resultsCard.resultsDate);
  return formatted || null;
};

export const LabResultsCard = ({
  resultsCard,
  medicationName,
  productIconUrl,
}: LabResultsCardProps) => {
  const { t } = useTranslation("care", { useSuspense: false });

  const title = resolveTitle(resultsCard, t);
  const formattedDate = resolveFormattedDate(resultsCard);
  const dateLine = formattedDate
    ? t("detail.resultsCard.dateLine", { date: formattedDate })
    : null;
  const onViewResults = resolveViewResults(resultsCard);

  return (
    <Stack spacing={0} width="100%" alignItems="stretch">
      <Box data-cy="lab-results-card" width="100%">
        <TreatmentProductLockup
          variant="labResults"
          title={title}
          subtitle={dateLine ?? undefined}
          imageUrl={productIconUrl}
          imageAlt={medicationName}
          productBackgroundColor={CompassColor.sand01}
        />
      </Box>
      {onViewResults && (
        <Box pt={4} pb={2}>
          <LabResultsCta
            medicationName={medicationName}
            onViewResults={onViewResults}
          />
        </Box>
      )}
    </Stack>
  );
};
